import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaLock } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { LoginForInsightsPrompt } from './TrackPickButton';

const shimmer = keyframes`
  0% {
    background-position: -120% 0;
  }
  100% {
    background-position: 120% 0;
  }
`;

const Wrap = styled.div`
  margin-top: ${(p) => (p.$flushTop ? 0 : '0.25rem')};
`;

/** Matches compact ShapRadarChart container feel so the slot reads as “chart was here”. */
const Shell = styled.div`
  position: relative;
  padding: 4px 8px 2px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.cardBg || p.theme.tertiaryBg};
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
`;

const Title = styled.h4`
  margin: 0 0 3px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: ${(p) => p.theme.primaryText};
`;

/** Fixed height so the blurred skeleton cannot extend a tall “tail” below the caption. */
const PreviewBlock = styled.div`
  position: relative;
  height: 44px;
  overflow: hidden;
`;

const BlurLayer = styled.div`
  filter: blur(4px);
  opacity: 0.5;
  user-select: none;
  pointer-events: none;
  transform: scale(0.99);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 2px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SkeletonLabel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  height: 7px;
  border-radius: 2px;
  max-width: ${(p) => p.$mw}px;
  background: linear-gradient(
    90deg,
    ${(p) => p.theme.border}33 0%,
    ${(p) => p.theme.border}88 45%,
    ${(p) => p.theme.border}33 100%
  );
  background-size: 220% 100%;
  animation: ${shimmer} 2.4s ease-in-out infinite;
`;

const SkeletonBar = styled.div`
  flex: 0 0 56px;
  height: 6px;
  border-radius: 2px;
  background: ${(p) => p.theme.chartGrid || p.theme.border};
  opacity: 0.45;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  padding: 0 6px 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    ${(p) => p.$scrim} 42%,
    ${(p) => p.$scrim} 100%
  );
  pointer-events: none;
`;

const LockBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  color: ${(p) => p.theme.primaryText};
  background: ${(p) => p.theme.tertiaryBg};
  border: 1px solid ${(p) => p.theme.border};
  box-shadow: ${(p) => p.theme.shadow || '0 2px 8px rgba(0,0,0,0.18)'};
`;

const TeaserCaption = styled.p`
  margin: 0;
  width: 100%;
  text-align: center;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TeaserCaptionInner = styled.span`
  display: inline-block;
  font-size: clamp(7.5px, 2.5vw, 9px);
  line-height: 1.25;
  color: ${(p) => p.theme.secondaryText};
  white-space: nowrap;
`;

/** Label skeleton widths (px) — fewer rows + clip keeps the block short under the caption. */
const SKELETON_LABEL_WIDTHS = [118, 152, 96, 134];

/**
 * Placeholder “chart” for logged-out users when SHAP exists but is gated: suggests real
 * factor breakdowns live behind login without exposing values.
 *
 * @param {{ playerName?: string, variant?: 'card' | 'table' }} props
 */
function ModelInsightsLockedTeaser({ playerName, variant = 'table' }) {
  const { theme } = useTheme();
  const displayName = playerName?.trim() || 'this player';
  const isCard = variant === 'card';
  const scrim =
    theme.cardBg ||
    theme.primaryBg ||
    theme.tertiaryBg ||
    'rgba(26, 32, 44, 0.92)';

  return (
    <Wrap $flushTop={isCard}>
      <Shell theme={theme}>
        <Title theme={theme}>Model insights for {displayName}</Title>
        <PreviewBlock>
          <BlurLayer>
            {SKELETON_LABEL_WIDTHS.map((mw, i) => (
              <Row key={`sk-${i}`}>
                <SkeletonLabel theme={theme} $mw={mw} />
                <SkeletonBar theme={theme} />
              </Row>
            ))}
          </BlurLayer>
          <Overlay $scrim={scrim} theme={theme}>
            <LockBadge theme={theme}>
              <FaLock aria-hidden size={12} />
              Insight breakdown
            </LockBadge>
            <TeaserCaption>
              <TeaserCaptionInner theme={theme}>
                See the top features impacting the model&apos;s prediction
              </TeaserCaptionInner>
            </TeaserCaption>
          </Overlay>
        </PreviewBlock>
      </Shell>
      <LoginForInsightsPrompt theme={theme} variant={variant} />
    </Wrap>
  );
}

export default ModelInsightsLockedTeaser;
